import React, { useState, useMemo } from 'react'
import { Grid, Box, Typography, Card, CardActionArea, CardMedia, CardContent, Badge, Button, IconButton } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import BookCard from './BookCard'
import { DEFAULT_BOOK_IMAGE } from '../constants'

/**
 * Composant de grille affichant une liste de livres
 */
const BooksGrid = ({ books, refresh, setRefresh, onEdit, viewMode = 'default', onSeriesSelect }) => {
  const [selectedSeries, setSelectedSeries] = useState(null)

  const handleSeriesSelect = (series) => {
    setSelectedSeries(series)
    if (onSeriesSelect) {
      onSeriesSelect(!!series)
    }
  }

  const { seriesGroups, standaloneBooks } = useMemo(() => {
    const groups = {}
    const standalone = []
    
    books.forEach(book => {
      if (book.series?.name) {
        if (!groups[book.series.name]) {
          groups[book.series.name] = {
            name: book.series.name,
            id: book.series.id,
            books: []
          }
        }
        groups[book.series.name].books.push(book)
      } else {
        standalone.push(book)
      }
    })

    // Sort books inside groups by volume_number
    Object.values(groups).forEach(group => {
      group.books.sort((a, b) => (a.volume_number || 0) - (b.volume_number || 0))
    })

    return {
      seriesGroups: Object.values(groups).sort((a, b) => a.name.localeCompare(b.name)),
      standaloneBooks: standalone
    }
  }, [books])

  // Reset selected series when viewMode changes
  React.useEffect(() => {
    if (viewMode !== 'series') {
      handleSeriesSelect(null)
    }
  }, [viewMode])

  if (viewMode === 'series' && selectedSeries) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <IconButton onClick={() => handleSeriesSelect(null)} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">Série : {selectedSeries.name}</Typography>
        </Box>
        <Grid container spacing={2} alignItems="stretch">
          {selectedSeries.books.map((book) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={book.id}>
              <BookCard book={book} refresh={refresh} setRefresh={setRefresh} onEdit={onEdit} />
            </Grid>
          ))}
        </Grid>
      </Box>
    )
  }

  if (viewMode === 'series') {
    return (
      <Grid container spacing={2} alignItems="stretch">
        {seriesGroups.map((group) => {
          const firstBook = group.books[0]
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={`series-${group.name}`}>
              <Badge badgeContent={group.books.length} color="primary" sx={{ width: '100%', '& .MuiBadge-badge': { right: 20, top: 20, zIndex: 2 } }}>
                <Card
                  elevation={0}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    height: 380,
                    margin: 'auto',
                    marginBottom: 2,
                    border: '2px solid',
                    borderColor: 'divider',
                    transition: 'transform 0.2s, border-color 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <CardActionArea
                    onClick={() => handleSeriesSelect(group)}
                    sx={{ display: 'flex', flexDirection: 'column', height: '100%', alignItems: 'stretch' }}
                  >
                    <Box sx={{ position: 'relative', width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: 'action.hover' }}>
                      <CardMedia
                        component="img"
                        image={firstBook?.image_url || DEFAULT_BOOK_IMAGE}
                        alt={group.name}
                        loading="lazy"
                        sx={{ width: '100%', height: 260, objectFit: 'contain', p: 2 }}
                      />
                    </Box>
                    <CardContent sx={{ height: 120, py: 2, px: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 1 }}>Série</Typography>
                      <Typography variant="h6" fontWeight="bold" align="center" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {group.name}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Badge>
            </Grid>
          )
        })}
        {standaloneBooks.map((book) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={book.id}>
            <BookCard book={book} refresh={refresh} setRefresh={setRefresh} onEdit={onEdit} />
          </Grid>
        ))}
      </Grid>
    )
  }

  return (
    <Grid container spacing={2} alignItems="stretch">
      {books.map((book) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={book.id}>
          <BookCard book={book} refresh={refresh} setRefresh={setRefresh} onEdit={onEdit} />
        </Grid>
      ))}
    </Grid>
  )
}

export default BooksGrid
